import {
  NEWEST_OVERVIEW_WINDOW,
  OverviewWindow,
  TRAINING_OVERVIEW_WINDOW_DAYS,
  TrainingOverviewModel,
  canShowEarlier,
  canShowLater,
  showEarlierStep,
  showLaterStep,
} from './training-overview';
import { TrainingOverviewLoadResult } from './training-overview-data.service';

/** the key a section's window is kept under: its workout's index, or this for "Other sessions" */
const OTHER_SESSIONS_KEY = 'other';

function sectionKey(planReference: number | undefined): number | string {
  return planReference ?? OTHER_SESSIONS_KEY;
}

/** per section of a model, whether its "Show Earlier" and "Show Later" buttons can do anything */
export interface TrainingOverviewPagingButtons {
  canShowEarlier: boolean[];
  canShowLater: boolean[];
}

/** what a press on "Show Earlier" or "Show Later" asks of the host */
export interface TrainingOverviewPagingAnswer {
  /** true when the loaded logs hold nothing older: load again, with `windowDays` already grown */
  needsReload: boolean;
  /** when no reload is needed: the same logs, re-sliced in memory, to show */
  model?: TrainingOverviewModel;
}

/**
 * The paging state of one Training Overview (#6676): which sessions each workout card shows, how
 * many days of logs are loaded, and what the "Show Earlier" and "Show Later" buttons do. Both hosts
 * use it, the Training Overview page and the coach review's Training tab, so they page the same way.
 *
 * Each card pages on its own. A card opens on its newest 4 sessions; a press pins it to an anchor
 * (its oldest shown session), so a session that syncs in later does not shift what the person is
 * looking at. A press inside the loaded sessions re-slices them in memory (`reshapeAsync`) and reads
 * no logs. Only a press that goes past the oldest loaded session, with an older finished workout on
 * the device, grows the loaded days by `TRAINING_OVERVIEW_WINDOW_DAYS` and asks the host to load
 * again; the card then pages back once the new logs are in. The rules themselves are the pure
 * functions in `training-overview.ts`. See design-docs/coaching-dashboard.md, "Phase 3: the
 * Training Overview".
 */
export class TrainingOverviewPaging {
  /** how many days back the logs are loaded; grows when "Show Earlier" runs past the loaded sessions */
  windowDays = TRAINING_OVERVIEW_WINDOW_DAYS;
  /** true when a finished workout older than the loaded days is on this device */
  mayHaveOlder = false;
  /** true while logs older than the first 3 months are still downloading; the loaded days then never grow */
  olderLogsDownloading = false;

  /** each card's window, by `sectionKey`; absent means Newest */
  private windowByKey = new Map<number | string, OverviewWindow>();
  /** the cards whose "Show Earlier" needed older logs: they page back once the next load is in */
  private pageBackAfterLoad = new Set<number | string>();
  /** the last load, whose logs a press re-slices */
  private loaded?: TrainingOverviewLoadResult;

  /** the window of each section; pass it to `TrainingOverviewDataService.loadAsync` */
  readonly windowFor = (planReference: number | undefined): OverviewWindow =>
    this.windowByKey.get(sectionKey(planReference)) ?? NEWEST_OVERVIEW_WINDOW;

  /**
   * Takes a finished load: remembers its logs for later presses, and pages back each card whose
   * "Show Earlier" asked for these older logs. Returns the model to show.
   */
  async acceptLoadAsync(result: TrainingOverviewLoadResult): Promise<TrainingOverviewModel> {
    this.loaded = result;
    this.mayHaveOlder = result.mayHaveOlder;
    this.olderLogsDownloading = result.olderLogsDownloading;
    let model = result.model;
    if (this.pageBackAfterLoad.size > 0) {
      for (const section of model.sections) {
        const key = sectionKey(section.planReference);
        // if older sessions arrived, the card was pinned to its old oldest: now step back from it
        if (this.pageBackAfterLoad.has(key) && section.firstShownIndex > 0) {
          this.windowByKey.set(key, showEarlierStep(section, false).window);
        }
      }
      this.pageBackAfterLoad.clear();
      model = await result.reshapeAsync(this.windowFor);
    }
    return this.noteShown(model);
  }

  /** "Show Earlier" on one card of the shown model */
  async showEarlierAsync(model: TrainingOverviewModel, sectionIndex: number): Promise<TrainingOverviewPagingAnswer> {
    const section = model.sections[sectionIndex];
    if (!section || !this.loaded) return { needsReload: false, model };
    const key = sectionKey(section.planReference);
    const step = showEarlierStep(section, this.mayHaveOlder);
    this.windowByKey.set(key, step.window);
    if (step.needsOlderLogs) {
      this.windowDays += TRAINING_OVERVIEW_WINDOW_DAYS;
      this.pageBackAfterLoad.add(key);
      return { needsReload: true };
    }
    return { needsReload: false, model: this.noteShown(await this.loaded.reshapeAsync(this.windowFor)) };
  }

  /** "Show Later" on one card of the shown model; never needs more logs */
  async showLaterAsync(model: TrainingOverviewModel, sectionIndex: number): Promise<TrainingOverviewPagingAnswer> {
    const section = model.sections[sectionIndex];
    if (!section || !this.loaded) return { needsReload: false, model };
    this.windowByKey.set(sectionKey(section.planReference), showLaterStep(section));
    return { needsReload: false, model: this.noteShown(await this.loaded.reshapeAsync(this.windowFor)) };
  }

  /**
   * Which paging buttons each card of `model` shows. The host asks for them when it shows the model,
   * after its own check that the answer is not stale, so a press whose answer was dropped can never
   * leave the buttons of a model that is not on screen.
   */
  buttonsFor(model: TrainingOverviewModel): TrainingOverviewPagingButtons {
    return {
      canShowEarlier: model.sections.map(section => canShowEarlier(section, this.mayHaveOlder)),
      canShowLater: model.sections.map(section => canShowLater(section)),
    };
  }

  /** keeps each card's window as the model resolved it: a gone anchor falls back to Newest */
  private noteShown(model: TrainingOverviewModel): TrainingOverviewModel {
    for (const section of model.sections) {
      this.windowByKey.set(sectionKey(section.planReference), section.window);
    }
    return model;
  }
}
